import { Button, Dialog, Fade, Stack } from "@mui/material";
import { type FC, useContext } from "react";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { RequestContext } from "../../contexts/RequestProvider";
import UploadImageInput from "../form-components/upload-image-input/UploadImageInput";
import { BackgroundImageCatalog } from "../organization-background-images/BackgroundImageCatalog";
import { useOrganizationBackgroundImages } from "../organization-background-images/useOrganizationBackgroundImages";

interface AddEventImageProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  imageId?: string;
  onImageIdChange?: (imageId: string | undefined) => void;
  onUploadImage?: (image: File) => void;
  isUploading?: boolean;
}

export const AddEventImage: FC<AddEventImageProps> = ({
  isOpen,
  setIsOpen,
  imageId,
  onImageIdChange,
  onUploadImage,
  isUploading = false,
}) => {
  const request = useContext(RequestContext);
  const currentImageId = imageId ?? request?.currentEvent.imageId;
  const { currOrganization } = useOrganization();

  const { data: backgroundImages } = useOrganizationBackgroundImages(
    currOrganization?.id,
    isOpen,
  );

  const fileInputId = "opportunity-background-file-input";

  const applyImage = (nextImageId: string) => {
    if (onImageIdChange) onImageIdChange(nextImageId);
    else {
      request?.setCurrentEvent((prev) => ({
        ...prev,
        imageId: nextImageId,
      }));
    }
    setIsOpen(false);
  };

  return (
    <>
      <Dialog
        disablePortal
        className="images-dialog"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 400 }}
        PaperProps={{
          className: "images-dialog-paper",
          sx: {
            overflow: "auto",
            width: "min(960px, 94vw)",
            maxWidth: "960px",
            maxHeight: "85vh",
            alignItems: "stretch",
            p: 2,
          },
        }}
      >
        <Stack direction="row" sx={{ width: "100%", mb: 1 }}>
          <Button
            variant="outlined"
            disabled={isUploading}
            onClick={() => document.getElementById(fileInputId)?.click()}
          >
            {isUploading ? "מעלה תמונה..." : "העלאת תמונה"}
          </Button>
          <UploadImageInput
            inputId={fileInputId}
            onImageUpload={onUploadImage}
            allowFullImageUpload={false}
          />
        </Stack>
        <BackgroundImageCatalog
          key={isOpen ? "open" : "closed"}
          organizationId={currOrganization?.id}
          images={backgroundImages}
          preferredSubCategoryId={request?.currentEvent.subCategoryId ?? undefined}
          preferredCategoryIds={(request?.currentEvent.selectedCategories ?? []).map(
            (category) => (typeof category === "string" ? category : category.id),
          )}
          selectedImageId={currentImageId}
          onSelectImage={applyImage}
        />
      </Dialog>
    </>
  );
};
